<template>
  <div v-if="shifts.length > 0" class="extra-shifts-component">
    <h2>
      <span>{{ $t("ETAB.PAGES.EXTRA.@SHIFTS_TITLE") }}</span>
      <a @click="$emit('openRecruitmentRecap')">
        {{ $t("ETAB.PAGES.EXTRA.@OPEN_RECAP") }}
      </a>
    </h2>
    <!-- Shifts -->
    <article>
      <header>
        <h3>{{ missionQualificationDisplay }}</h3>
        <p>{{ missionAddressDisplay }}</p>
      </header>

      <p v-if="extra.getCanceledComment().length > 0" class="extra-shift-canceled-comment">
        <label>{{ $t("ETAB.PAGES.EXTRA.@CANCELED_COMMENT_BY", { name: extra.getIdentity().firstname }) }}</label>
        <span class="extra-comment-quotes">
          {{ extra.getCanceledComment() }}
        </span>
      </p>

      <section v-for="(shift, index) in shifts" v-show="index < SHIFT_AMOUNT_DISPLAY || showAll" :key="shift.id">
        <h4>{{ shift.getDayDisplay() }}</h4>
        <!-- Extra canceled -->
        <p v-if="shift.isCanceledByExtra()" class="text-negative">
          <QIcon name="icon-f-alert-triangle" size="22px" />
          {{ $t("ETAB.PAGES.EXTRA.@EXTRA_CANCELED") }}
        </p>
        <!-- Extra missing -->
        <p v-if="shift.isExtraMissing()" class="text-negative">
          <QIcon name="icon-f-alert-triangle" size="22px" />
          {{ $t("ETAB.PAGES.EXTRA.@EXTRA_MISSING") }}
        </p>
        <!-- Schedule -->
        <template v-if="!shift.isCanceledByExtra() && !shift.isExtraMissing()">
          <p v-if="shift.getBreakDuration() > 0" class="extra-shifts-labels">
            {{ $t("ETAB.PAGES.EXTRA.@BREAK_LABEL") }}
          </p>
          <p>
            <QIcon name="icon-f-clock" size="22px" color="grey" />
            <b>{{ shift.getStartTimeDisplay() }}</b>
            <QIcon name="icon-f-arrow-right" size="22px" color="grey" />
            <b>{{ shift.getEndTimeDisplay() }}</b>
            <b>{{ shift.getBreakDisplay() }}</b>
          </p>
        </template>
      </section>
      <section v-if="!showAll && shifts.length > SHIFT_AMOUNT_DISPLAY" class="extra-shifts-show-all">
        <QBtn :label="$t('ETAB.PAGES.EXTRA.@SHOW_MORE')" class="full-width" flat color="accent" no-caps @click="showAllShifts" />
      </section>
      <footer>
        <h4>{{ $t("ETAB.PAGES.EXTRA.@THEORICAL_PRICE", { price: extra.getTheoricalPrice() }) }}</h4>
      </footer>
    </article>
  </div>
</template>

<script>
  import { ExtraModel } from "@core/models";
  import { GETTER_RECRUITMENT_GET_CURRENT } from "@etab/stores/types";
  import { useRecruitmentStore } from "@etab/stores/recruitment";
  import { mapState } from "pinia";

  const SHIFT_AMOUNT_DISPLAY = 4;

  export default {
    props: {
      extra: {
        type: Object,
        default: () => new ExtraModel(),
      },
    },
    emits: ["openRecruitmentRecap"],
    data() {
      return {
        showAll: false,
      };
    },
    computed: {
      ...mapState(useRecruitmentStore, {
        recruitment: GETTER_RECRUITMENT_GET_CURRENT,
      }),
      shifts() {
        return this.extra.getShifts() || [];
      },
      missionQualificationDisplay() {
        return this.$t("ETAB.PAGES.EXTRA.@MISSION_QUALIFICATION", { qualification: this.extra.getMissionQualification().getLabel() });
      },
      missionDatesDisplay() {
        const dates = this.extra.getMissionDates();
        if (dates) {
          const start = dates.start.format("DD MMMM");
          const end = dates.end.format("DD MMMM");
          if (dates.start.isSame(dates.end, "day")) {
            return this.$t("ETAB.PAGES.EXTRA.@MISSION_DATES_SAME_DAY", { start });
          }
          return this.$t("ETAB.PAGES.EXTRA.@MISSION_DATES", { start, end });
        }
        return "";
      },
      missionAddressDisplay() {
        const address = this.recruitment.getCurrentEtablissement().getFormattedAddress();
        return this.$t("ETAB.PAGES.RECRUITMENT.TILE.@MISSION_ADDRESS_DISPLAY", { address });
      },
    },
    watch: {
      extra(newValue, oldValue) {
        this.resetState();
      },
    },
    created() {
      this.SHIFT_AMOUNT_DISPLAY = SHIFT_AMOUNT_DISPLAY;
    },
    methods: {
      showAllShifts() {
        this.showAll = true;
      },
      resetState() {
        this.showAll = false;
      },
    },
  };
</script>

<style lang="scss">
  .extra-shifts-component {
    h2 {
      color: $secondary-light;
      margin: 25px 0 15px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      a {
        color: $primary-light;
        font-family: $common-font-family;
        font-size: 14px;
        font-weight: normal;
        text-decoration: underline;
        line-height: initial;
        letter-spacing: initial;
        cursor: pointer;
      }
    }

    article {
      border-radius: 4px;
      overflow: hidden;

      > header {
        background-color: $secondary-medium;
        padding: 15px;
        color: white;

        h3,
        p {
          margin: 0;
          line-height: 1.5;
        }
      }

      > footer {
        background-color: $secondary-medium;
        color: white;
        padding: 5px 15px;

        h4 {
          margin: 0;
          text-align: center;
          font-size: 15px;
        }
      }

      .extra-shift-canceled-comment {
        color: $negative;
        padding: 15px;
        margin: 0;

        label {
          display: block;
          font-weight: bold;
          margin-bottom: 5px;
        }
      }

      > section {
        padding: 15px;
        position: relative;
        background-color: white;

        &:not(:first-of-type):before {
          content: "";
          position: absolute;
          top: 0;
          left: 10px;
          right: 0;
          display: block;
          border-bottom: 1px solid $lightgrey;
        }

        h4 {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin: 0 0 5px;
          font-size: 1.1em;

          .extra-shift-btn-action {
            padding: 0;

            .q-btn__content > *:not(i) {
              text-decoration: underline;
            }
          }
        }

        p {
          margin: 0;
          display: flex;
          align-items: center;
          justify-content: left;

          > .q-icon {
            margin: 0 10px;

            &:first-of-type {
              margin-left: 0;
            }
          }

          > b {
            font-weight: normal;
            font-size: 1em;

            &:last-of-type {
              text-align: right;
              flex: 1;
              padding-right: 20px;
            }
          }

          &.extra-shifts-labels {
            position: absolute;
            font-size: 0.8em;
            right: 15px;
            top: 25px;
            font-size: 0.8em;
            color: $grey;
            justify-content: flex-end;
            padding-right: 20px;
          }
        }
      }

      .extra-shifts-show-all {
        padding: 10px;
      }
    }
  }
</style>
